import React from "react";
import { ArrowUpRight, MapPin, CalendarDays } from "lucide-react";
import { EVENTS } from "../lib/data";
import SectionNumber from "./SectionNumber";

export default function Events() {
  const ev = EVENTS[0];

  return (
    <section
      id="programok"
      data-testid="events-section"
      className="relative bg-pkcharcoal py-24 sm:py-32 overflow-hidden"
    >
      <SectionNumber n="05" variant="dark" />
      {/* Subtle red glow background */}
      <div
        className="absolute inset-0 opacity-30 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse at 80% 20%, rgba(217,4,41,0.35) 0%, transparent 50%), radial-gradient(ellipse at 10% 80%, rgba(217,4,41,0.15) 0%, transparent 50%)",
        }}
      />
      <div className="grain opacity-10" />

      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        {/* Section header */}
        <div className="grid md:grid-cols-12 gap-8 mb-16 items-end">
          <div className="md:col-span-7 reveal">
            <span className="eyebrow" style={{ color: "#FFA64D" }}>
              Következő esemény
            </span>
            <h2 className="font-serif text-white text-4xl sm:text-5xl lg:text-6xl mt-6 leading-[1.05]">
              Közelgő<br />
              <span className="italic text-pkred">tüzes élmény.</span>
            </h2>
          </div>
          <div className="md:col-span-5 reveal">
            <p className="text-white/65 text-base sm:text-lg leading-relaxed">
              Balkáni vacsorák, tűz körüli történetek és családbarát programok.
              Jelentkezz időben – a tűz körüli helyek gyorsan elfogynak.
            </p>
          </div>
        </div>

        {/* Featured event */}
        <div
          id="workshopok"
          data-testid="featured-event"
          className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-stretch"
        >
          {/* Image */}
          <div className="lg:col-span-6 relative group overflow-hidden reveal min-h-[340px] lg:min-h-[520px]">
            <img
              src={ev.img}
              alt={ev.name}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-pkcharcoal/80 via-transparent to-transparent" />
            <div className="absolute top-6 left-6 date-badge">{ev.short}</div>
          </div>

          {/* Intro + meta + CTA */}
          <div className="lg:col-span-6 flex flex-col justify-center reveal">
            <div className="text-pkred text-[0.7rem] tracking-[0.3em] uppercase font-semibold hero-fire-label flex items-center gap-2">
              <CalendarDays className="w-4 h-4" strokeWidth={1.8} />
              {ev.date}
            </div>
            <h3 className="font-serif text-white text-3xl sm:text-4xl lg:text-5xl mt-4 leading-tight">
              {ev.name}
            </h3>
            <p className="text-white/70 text-base sm:text-lg mt-5 leading-relaxed">
              {ev.desc}
            </p>
            <div className="mt-6 flex items-center gap-2 text-white/50 text-sm">
              <MapPin className="w-4 h-4 text-pkred" strokeWidth={1.8} />
              Pakkaland · Vezseny
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-4 sm:items-center">
              <a
                href={ev.reservationHref}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="event-reserve-cta"
                className="btn-fire inline-flex items-center justify-center gap-2"
              >
                Foglalás
                <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
              </a>
              <p className="text-pkred/90 text-sm font-medium max-w-xs">
                {ev.note}
              </p>
            </div>
          </div>
        </div>

        {/* Program timeline */}
        <div className="mt-20 sm:mt-24">
          <div className="max-w-2xl mb-12 reveal">
            <span className="eyebrow" style={{ color: "#FFA64D" }}>
              A nap programja
            </span>
            <h3 className="font-serif text-white text-3xl sm:text-4xl mt-5 leading-tight">
              Egy nap, <span className="italic text-pkred">reggeltől estig.</span>
            </h3>
          </div>

          <ol className="relative border-l border-white/15 ml-2 sm:ml-3">
            {ev.program.map((item, i) => (
              <li
                key={item.time}
                data-testid={`program-step-${i}`}
                className="relative pl-8 sm:pl-12 pb-12 last:pb-0 reveal"
                style={{ transitionDelay: `${i * 60}ms` }}
              >
                {/* Dot */}
                <span className="absolute -left-[7px] top-1.5 w-3.5 h-3.5 rounded-full bg-pkred ring-4 ring-pkcharcoal" />
                <div className="flex flex-col sm:flex-row sm:items-baseline gap-1 sm:gap-6">
                  <div className="font-serif text-pkred text-2xl sm:text-3xl leading-none shrink-0 sm:w-28">
                    {item.time}
                  </div>
                  <div>
                    <h4 className="text-white text-lg sm:text-xl font-semibold leading-snug">
                      {item.title}
                    </h4>
                    {item.desc && (
                      <p className="text-white/60 text-sm sm:text-base mt-2 leading-relaxed max-w-2xl">
                        {item.desc}
                      </p>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>

        {/* Closing note + CTA */}
        <div className="mt-16 flex flex-col items-center text-center reveal">
          <p className="font-serif italic text-white/85 text-xl sm:text-2xl max-w-xl leading-relaxed">
            Nem egyszerűen egy étkezés – egy közös élmény.
          </p>
          <a
            href={ev.reservationHref}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="events-bottom-cta"
            className="btn-fire mt-8 inline-flex items-center gap-2"
          >
            Foglalás az eseményre
            <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
          </a>
        </div>
      </div>
    </section>
  );
}
